import { ArrowRightOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client/react';
import { Button, Card, Col, Descriptions, Row, Switch } from 'antd';
import { includes, nth } from 'lodash';
import moment from 'moment';
import React, { useState } from 'react';
import { useMedia } from 'react-use';
import { Active, EditButton, Inactive } from '../../../../../assets/svg';
import {
  ACCESSTYPE,
  ACCESSTYPE_KEYS,
  ADDONS,
  BREAKPOINTS,
  ORGANIZATION_TYPES,
  ROUTES,
  defaultDateFormat,
} from '../../../../../common/constants';
import { getInitialRfiAutoNumbering } from '../../../../../common/utils';
import Portal from '../../../../../components/Portal';
import useRouter from '../../../../../hooks/useRouter';
import DeleteModal from '../../../DeleteModal';
import { UPDATE_ORGANIZATION } from '../../../graphql/Mutations';
import { GET_ORGANIZATIONS } from '../../../graphql/Queries';

const Details = ({ data, fetchOrganizationData, pageFilter }) => {
  const fetchData = nth(data?.tenantList?.data, 0);
  const { navigate } = useRouter();
  const [showModal, setShowModal] = useState(false);
  const isDesktopViewport = useMedia(`(min-width: ${BREAKPOINTS.desktop}px)`);

  const [updateTenant] = useMutation(UPDATE_ORGANIZATION, {
    onError() {},
    refetchQueries: [
      {
        query: GET_ORGANIZATIONS,
        variables: {
          filter: { sortBy: { field: 'updatedAt', order: 'DESC' } },
        },
      },
    ],
  });

  return (
    <div>
      <Portal portalId="header-right-content">
        {showModal && (
          <DeleteModal
            showModal={showModal}
            setShowModal={setShowModal}
            title={fetchData?.isDisabled ? `Activate` : `Deactivate`}
            updateTenant={updateTenant}
            mutationId={fetchData?.id}
            isDisabled={fetchData?.isDisabled}
            fetchOrganizationData={fetchOrganizationData}
            name="Organization"
            subtitle="Organization"
          />
        )}
        <div className="action d-flex">
          <Button
            type={isDesktopViewport ? 'primary' : 'text'}
            shape={isDesktopViewport ? 'round' : 'circle'}
            className="mr-5"
            onClick={() => {
              setShowModal(true);
            }}
          >
            {/* eslint-disable no-nested-ternary */}
            {isDesktopViewport ? (
              fetchData?.isDisabled ? (
                `Activate`
              ) : (
                `Deactivate`
              )
            ) : fetchData?.isDisabled ? (
              <Active />
            ) : (
              <Inactive />
            )}
          </Button>
          <Button
            type={isDesktopViewport ? 'primary' : 'text'}
            shape={isDesktopViewport ? 'round' : 'circle'}
            icon={!isDesktopViewport && <EditButton />}
            onClick={() => {
              navigate(`${ROUTES.ORGANIZATION}/edit/${fetchData?.id}`, {
                state: { pageFilter },
              });
            }}
          >
            {isDesktopViewport && 'Edit'}
          </Button>
        </div>
      </Portal>
      <Row gutter={24}>
        <Col xs={24} lg={8}>
          <Card className="mb-10">
            <h1>Organization Details</h1>
            <Descriptions
              layout="vertical"
              column={1}
              className="details-owner-description"
            >
              <Descriptions.Item label="Organization Name">
                {fetchData?.organizationName}
              </Descriptions.Item>
              <Descriptions.Item label="System Admin Name">
                {fetchData?.ownerName}
              </Descriptions.Item>
              <Descriptions.Item label="System Admin Email">
                {fetchData?.adminEmail}
              </Descriptions.Item>
              <Descriptions.Item label="System Admin Mobile Number">
                {fetchData?.phoneNumber}
              </Descriptions.Item>
              <Descriptions.Item label="Organization Type">
                {ORGANIZATION_TYPES[fetchData?.organizationType] || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="TimeZone">
                {fetchData?.timeZone || '-'}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card className="mb-10">
            <h1>Subscription Details</h1>
            <Descriptions
              layout="vertical"
              column={1}
              className="details-owner-description"
            >
              <Descriptions.Item label="Current Plan">
                {fetchData?.planName}
              </Descriptions.Item>
              <Descriptions.Item label="Users">
                {`${fetchData?.userCount}/ ${fetchData?.userLimit}`}
              </Descriptions.Item>
              <Descriptions.Item label="Projects">
                {`${fetchData?.projectCount}/ ${fetchData?.projectLimit}`}
              </Descriptions.Item>
              <Descriptions.Item label="Valid Till">
                {fetchData?.subscription?.validTill
                  ? moment(fetchData?.subscription?.validTill).format(
                      defaultDateFormat,
                    )
                  : '-'}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card>
            <h1>Addon Services</h1>
            <div className="addon-div">
              <div className="font-weight-500 m-5">1. Access Type</div>
              {includes(fetchData?.access, ACCESSTYPE_KEYS.INSPECTION) && (
                <div className="sub-addon-div">
                  <div>a. {ACCESSTYPE.INSPECTION}</div>
                  <Switch checked />
                </div>
              )}
              {includes(fetchData?.access, ACCESSTYPE_KEYS.INSTRUCTION) && (
                <div className="sub-addon-div">
                  <div>b. {ACCESSTYPE.INSTRUCTION}</div>
                  <Switch checked />
                </div>
              )}
              {includes(fetchData?.access, ACCESSTYPE_KEYS.REGISTER) && (
                <div className="sub-addon-div">
                  <div>c. Register</div>
                  <Switch checked />
                </div>
              )}
              {includes(fetchData?.access, ACCESSTYPE_KEYS.REGISTER) && (
                <div className="sub-addon-div nested-addon-div">
                  <div>Register Limit</div>
                  <div>{fetchData?.projectRegisterLimit}</div>
                </div>
              )}
              {/* US #669 US1-9: the organisation's SUBMISSION ceiling. Gated on REGISTER access
                  ALONE and nested under `c. Register` beside Register Limit, mirroring
                  OrganizationForm, so a Register-only organisation can see its own saved value.
                  A separate value from the verifier-level cap: US1-19 keeps the 3-level limit
                  unaffected by it. At 1 a System Admin only ever sees "One time" on Assign
                  Verifiers. */}
              {includes(fetchData?.access, ACCESSTYPE_KEYS.REGISTER) && (
                <div className="sub-addon-div nested-addon-div">
                  <div>Register Submissions</div>
                  <div>{fetchData?.registerMaxSubmissions || 3}</div>
                </div>
              )}
              <div className="font-weight-500 m-5">2. Whatsapp</div>
              {includes(fetchData?.access, ACCESSTYPE_KEYS.INSPECTION) && (
                <div className="sub-addon-div">
                  <div>a. {ACCESSTYPE.INSPECTION}</div>
                  <Switch
                    checked={includes(fetchData?.addOn, ADDONS.WHATSAPP)}
                  />
                </div>
              )}
              {includes(fetchData?.access, ACCESSTYPE_KEYS.INSTRUCTION) && (
                <div className="sub-addon-div">
                  <div>b. {ACCESSTYPE.INSTRUCTION}</div>
                  <Switch
                    checked={includes(
                      fetchData?.addOn,
                      ADDONS.INSTRUCTION_WHATSAPP,
                    )}
                  />
                </div>
              )}
              <div className="font-weight-500 m-5">3. Photo limit</div>
              {includes(fetchData?.access, ACCESSTYPE_KEYS.INSPECTION) && (
                <div className="sub-addon-div">
                  <div>a. {ACCESSTYPE.INSPECTION}</div>
                  {fetchData?.inspectionAssetLimit || 5}
                </div>
              )}
              {includes(fetchData?.access, ACCESSTYPE_KEYS.INSTRUCTION) && (
                <div className="sub-addon-div">
                  <div>b. {ACCESSTYPE.INSTRUCTION}</div>
                  {fetchData?.instructionAssetLimit || 5}
                </div>
              )}
              {includes(fetchData?.access, ACCESSTYPE_KEYS.REGISTER) && (
                <div className="sub-addon-div">
                  <div>c. Register Approver</div>
                  {fetchData?.registerAssetLimit || 5}
                </div>
              )}
              {includes(fetchData?.access, ACCESSTYPE_KEYS.INSPECTION) && (
                <>
                  <div className="font-weight-500 m-5">4. Approver Limit</div>
                  {includes(fetchData?.addOn, ADDONS.MULTI_LEVEL_APPROVAL) && (
                    <div className="sub-addon-div">
                      <div>a. {ACCESSTYPE.INSPECTION}</div>
                      {fetchData?.maxApprovalLevel || 5}
                    </div>
                  )}
                  {includes(fetchData?.addOn, ADDONS.MULTI_LEVEL_APPROVAL) &&
                    includes(fetchData?.access, ACCESSTYPE_KEYS.REGISTER) && (
                      <div className="sub-addon-div">
                        <div>b. Register</div>
                        {fetchData?.registerMaxApprovalLevel || 5}
                      </div>
                    )}
                  <div className="d-flex justify-between mt-5 align-center">
                    <div className="font-weight-500 m-5">5. Checklist</div>
                  </div>
                  <div className="sub-addon-div">
                    <div>a. Stage Limit</div>
                    {fetchData?.featureConfig?.stageMaxLimit || 5}
                  </div>
                  <div className="d-flex justify-between mt-5 align-center">
                    <div className="font-weight-500 m-5">
                      6. Inspection Gallery access
                    </div>
                    <Switch
                      checked={includes(
                        fetchData?.addOn,
                        ADDONS.INSPECTION_GALLERY_ACCESS,
                      )}
                    />
                  </div>
                  <div className="d-flex justify-between mt-5 align-center">
                    <div className="font-weight-500 m-5">7. Target</div>
                    <Switch
                      checked={includes(
                        fetchData?.addOn,
                        ADDONS.TARGET_ACTUAL_METRIC,
                      )}
                    />
                  </div>
                </>
              )}
              <div className="d-flex justify-between mt-5 align-center">
                <div className="font-weight-500 m-5">8. Nomenclature</div>
                <Switch
                  checked={includes(fetchData?.addOn, ADDONS.NOMENCLATURE)}
                />
              </div>
              {includes(fetchData?.access, ACCESSTYPE_KEYS.INSTRUCTION) && (
                <>
                  <div className="d-flex justify-between mt-5 align-center">
                    <div className="font-weight-500 m-5">
                      9. {ACCESSTYPE.INSTRUCTION} Tags
                    </div>
                    <Switch checked={fetchData?.featureConfig?.issueTag} />
                  </div>
                  <div className="d-flex justify-between mt-5 align-center">
                    <div className="font-weight-500 m-5">
                      10. {ACCESSTYPE.INSTRUCTION} Recommendation
                    </div>
                    <Switch
                      checked={fetchData?.featureConfig?.issueRecommendation}
                    />
                  </div>
                </>
              )}
              {includes(fetchData?.access, ACCESSTYPE_KEYS.INSPECTION) && (
                <div className="d-flex justify-between mt-5 align-center">
                  <div className="font-weight-500 m-5">11. RFI</div>
                  <Switch checked={includes(fetchData?.addOn, ADDONS.RFI)} />
                </div>
              )}
              {includes(fetchData?.access, ACCESSTYPE_KEYS.INSPECTION) &&
                includes(fetchData?.addOn, ADDONS.RFI) && (
                  <div className="sub-addon-div">
                    <div>a. RFI Auto-Numbering</div>
                    <Switch checked={getInitialRfiAutoNumbering(fetchData)} />
                  </div>
                )}
              <div className="d-flex justify-between mt-5 align-center">
                <div className="font-weight-500 m-5">12. Report Setup</div>
                <Switch
                  checked={includes(fetchData?.addOn, ADDONS.REPORT_CONFIG)}
                />
              </div>
              <div className="d-flex justify-between mt-5 align-center">
                <div className="font-weight-500 m-5">
                  13. Report DigiQC branding
                </div>
                <Switch checked={fetchData?.systemBranding} />
              </div>
              {includes(fetchData?.access, ACCESSTYPE_KEYS.INSTRUCTION) && (
                <>
                  <div className="d-flex justify-between mt-5 align-center">
                    <div className="font-weight-500 m-5">
                      14. {ACCESSTYPE.INSTRUCTION} Type
                    </div>
                    <Switch
                      checked={includes(
                        fetchData?.addOn,
                        ADDONS.INSTRUCTION_TYPE,
                      )}
                    />
                  </div>
                  <div className="d-flex justify-between mt-5 align-center">
                    <div className="font-weight-500 m-5">15. Quality Cost</div>
                    <Switch
                      checked={includes(fetchData?.addOn, ADDONS.QUALITY_COST)}
                    />
                  </div>
                </>
              )}
              {includes(fetchData?.access, ACCESSTYPE_KEYS.INSPECTION) &&
                includes(fetchData?.access, ACCESSTYPE_KEYS.INSTRUCTION) && (
                  <div className="d-flex justify-between mt-5 align-center">
                    <div className="font-weight-500 m-5">
                      16. Fail Checkpoints <ArrowRightOutlined /> NCR
                    </div>
                    <Switch
                      checked={
                        fetchData?.tenantConfig?.ncrInstructionRequiredOnEqcFail
                      }
                    />
                  </div>
                )}
              {includes(fetchData?.access, ACCESSTYPE_KEYS.INSTRUCTION) && (
                <div className="d-flex justify-between mt-5 align-center">
                  <div className="font-weight-500 m-5">
                    17. Issue escalation rule
                  </div>
                  <Switch
                    checked={includes(fetchData?.addOn, ADDONS.ISSUE_ESCALATION)}
                  />
                </div>
              )}
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Details;
